import {
  BasePageModel,
  basePageToLocal,
  TrainingPhraseConfig,
  Attribute,
  ValueSuggestionData,
  DataQuery,
  ResponseConfig,
  FrameItem,
  ValueAssociation,
  ValueCheck,
  trainingPhraseToLocal,
  attributeToLocal,
  valueSuggestionToLocal,
  dataQueryToLocal,
  responseToLocal,
  FrameItemToLocal,
  valueAssociationToLocal,
  valueCheckToLocal,
  ConfirmationInfo,
  confirmationInfoToLocal,
  trainingPhraseToServer,
  ReferenceNode,
  GenericType,
  genericTypeToLocal,
  genericTypeToServer,
  ExternalType,
  externalTypeToLocal,
  externalTypeToServer,
  ServiceSlot as ServiceSlot,
  serviceSlotToLocal,
  serviceSlotToServer,
  referenceNodeToLocal,
  referenceNodeToServer,
  StateUpdateInfo,
  stateUpdateInfoToLocal,
  stateUpdateInfoToServer,
  FrameMapping,
  frameMappingToLocal,
  FramelyAction,
  actionToLocal,
  actionToServer,
  DialogAct,
  dialogActToLocal,
  dialogActToServer,
  PostFillAction,
  postFillActionToLocal,
  DefaultGenericTypes,
  genericTypesToLocal,
  AugmentMeta,
  augmentMetaToLocal,
  augmentMetaToServer
} from '@interface/index';

export interface CronData {
  dataQueryId?: string;
  schedule: string;
}

export type FrameReturn = {
  type?: ReferenceNode;
  multiValued?: boolean;
  value: string;
  valueComment: string;
  async: boolean;
  actions: FramelyAction[];
};

export interface IntentInfo extends BasePageModel {
  subOfWeakTypeId?: string;
  useExpression?: ReferenceNode;
  description: string;
  triggeredByDU: boolean;
  triggers: string[];
  dialogAct?: boolean;
  qualification: string;
  externalQualification: boolean;
  trainingPhrases: TrainingPhraseConfig[];
  negativePhrases: TrainingPhraseConfig[];
  entailmentPhrases: TrainingPhraseConfig[];
  abortPhrases: TrainingPhraseConfig[];
  abortNegativePhrases: TrainingPhraseConfig[];
  abortEntailmentPhrases: TrainingPhraseConfig[];
  attributes: Attribute[];
  slotPosition: {
    pageId: string;
  }[];
  nestedSlotCustomizations: Attribute[];
  responsePosition: {
    pageId: string;
  }[];
  valueSuggestions: ValueSuggestionData[];
  dataQueries: DataQuery[];
  services: ServiceSlot[];
  branching: boolean;
  responses: ResponseConfig[];
  inheritedFrames: FrameItem[];
  genericTypes: GenericType[];
  externalTypes: ExternalType[];
  associations: ValueAssociation[];
  valueChecks: ValueCheck[];
  confirmations: ConfirmationInfo[];
  stateUpdates: StateUpdateInfo[];
  mappings: FrameMapping[];
  dialogActs: DialogAct[];
  postFillActions: PostFillAction[];
  cronData?: CronData;
  returnInfo?: FrameReturn;
  booleanGatePrompts?: string[];
  booleanGateExpressionsYes?: TrainingPhraseConfig[];
  booleanGateExpressionsNo?: TrainingPhraseConfig[];
  enableFallback?: boolean;
  fallbackInfo?: AugmentMeta;
  soft?: boolean;
  softResponse?: AugmentMeta;
  masterSelf?: IntentInfo;
}

export function intentInfoToLocal(data: any): IntentInfo {
  return {
    ...basePageToLocal(data),
    subOfWeakTypeId: data.sub_of_weak_type_id,
    useExpression: data.use_expression && {
      id: data.use_expression.id,
      name: data.use_expression.name,
      type: data.use_expression.type
    },
    description: data.description,
    triggers: data.triggers,
    qualification: data.qualification,
    externalQualification: data.external_qualification,
    triggeredByDU: data.triggered_by_du,
    dialogAct: data.dialog_act,
    trainingPhrases: data.frame_expressions
      && data.frame_expressions.map(trainingPhraseToLocal),
    negativePhrases: data.negative_expressions
      && data.negative_expressions.map(trainingPhraseToLocal),
    entailmentPhrases: data.entailment_expressions
      && data.entailment_expressions.map(trainingPhraseToLocal),
    abortPhrases: data.abort_expressions
      && data.abort_expressions.map(trainingPhraseToLocal),
    abortNegativePhrases: data.abort_negative_expressions
      && data.abort_negative_expressions.map(trainingPhraseToLocal),
    abortEntailmentPhrases: data.abort_entailment_expressions
      && data.abort_entailment_expressions.map(trainingPhraseToLocal),
    attributes: data.attributes
      && data.attributes.map(attributeToLocal) || [],
    nestedSlotCustomizations: data.nested_slot_customizations && data.nested_slot_customizations.map(attributeToLocal),
    valueSuggestions: data.value_suggestions
      && data.value_suggestions.map(valueSuggestionToLocal),
    dataQueries: data.data_queries
      && data.data_queries.map(dataQueryToLocal),
    services: (data.services || []).map(serviceSlotToLocal),
    branching: data.branching,
    responses: data.responses
      && data.responses.map(responseToLocal),
    inheritedFrames: data.inherited_frames
      && data.inherited_frames.map(FrameItemToLocal),
    genericTypes: genericTypesToLocal(data.generic_types),
    externalTypes: data.external_types
      && data.external_types.map(externalTypeToLocal),
    associations: data.associations
      && data.associations.map(valueAssociationToLocal),
    valueChecks: data.value_checks
      && data.value_checks.map(valueCheckToLocal),
    confirmations: (data.confirmations || []).map(confirmationInfoToLocal),
    stateUpdates: (data.state_updates || []).map(stateUpdateInfoToLocal),
    mappings: (data.mappings || []).map(frameMappingToLocal),
    dialogActs: (data.dialog_acts || []).map(dialogActToLocal),
    postFillActions: (data.post_fill_actions || []).map(postFillActionToLocal),
    cronData: data.cron_data && {
      dataQueryId: data.cron_data.data_query_id,
      schedule: data.cron_data.schedule
    },
    slotPosition: data.slot_position && data.slot_position.map((item: any) => ({
      pageId: item.page_id
    })) || [],
    responsePosition: data.response_position && data.response_position.map((item: any) => ({
      pageId: item.page_id
    })),
    returnInfo: data.return_info && {
      type: data.return_info.type && referenceNodeToLocal(data.return_info.type),
      multiValued: data.return_info.multi_valued,
      value: data.return_info.value || '',
      valueComment: data.return_info.value_comment,
      async: data.return_info.async,
      actions: data.return_info.actions && data.return_info.actions.map(actionToLocal) || []
    },
    booleanGatePrompts: data.boolean_gate_prompts,
    booleanGateExpressionsYes: data.boolean_gate_expressions_yes &&
      data.boolean_gate_expressions_yes.map(trainingPhraseToLocal),
    booleanGateExpressionsNo: data.boolean_gate_expressions_no &&
      data.boolean_gate_expressions_no.map(trainingPhraseToLocal),
    soft: data.soft,
    softResponse: data.soft_response && augmentMetaToLocal(data.soft_response),
    enableFallback: data.enable_fallback,
    fallbackInfo: data.fallback_info && augmentMetaToLocal(data.fallback_info),
    masterSelf: data.master_self && intentInfoToLocal(data.master_self)
  };
}

export function intentInfoToServer(data: IntentInfo) {
  return {
    id: data.id,
    name: data.name,
    comment: data.comment,
    use_expression: data.useExpression && {
      id: data.useExpression.id,
      name: data.useExpression.name,
      type: data.useExpression.type
    },
    branching: data.branching,
    description: data.description,
    qualification: data.qualification,
    external_qualification: data.externalQualification,
    triggered_by_du: data.triggeredByDU,
    triggers: data.triggers,
    dialog_act: data.dialogAct,
    services: (data.services || []).map(serviceSlotToServer),
    cron_data: data.cronData && {
      data_query_id: data.cronData.dataQueryId,
      schedule: data.cronData.schedule
    },
    generic_types: data.genericTypes &&
      data.genericTypes.map(genericTypeToServer),
    external_types: data.externalTypes &&
      data.externalTypes.map(externalTypeToServer),
    associations: data.associations &&
      data.associations.map(valueAssociationToLocal),
    frame_expressions: data.trainingPhrases &&
      data.trainingPhrases.map(trainingPhraseToServer),
    negative_expressions: data.negativePhrases &&
      data.negativePhrases.map(trainingPhraseToServer),
    entailment_expressions: data.entailmentPhrases &&
      data.entailmentPhrases.map(trainingPhraseToServer),
    abort_expressions: data.abortPhrases &&
      data.abortPhrases.map(trainingPhraseToServer),
    abort_negative_expressions: data.abortNegativePhrases &&
      data.abortNegativePhrases.map(trainingPhraseToServer),
    abort_entailment_expressions: data.abortEntailmentPhrases &&
      data.abortEntailmentPhrases.map(trainingPhraseToServer),
    return_info: data.returnInfo && {
      type: data.returnInfo.type && referenceNodeToServer(data.returnInfo.type),
      multi_valued: data.returnInfo.multiValued,
      value: data.returnInfo.value || '',
      value_comment: data.returnInfo.valueComment,
      async: data.returnInfo.async,
      actions: data.returnInfo.actions && data.returnInfo.actions.map(actionToServer) || []
    },
    boolean_gate_prompts: data.booleanGatePrompts,
    boolean_gate_expressions_yes: data.booleanGateExpressionsYes &&
      data.booleanGateExpressionsYes.map(trainingPhraseToServer),
    boolean_gate_expressions_no: data.booleanGateExpressionsNo &&
      data.booleanGateExpressionsNo.map(trainingPhraseToServer),
    soft: data.soft,
    soft_response: data.softResponse && augmentMetaToServer(data.softResponse),
    enable_fallback: data.enableFallback,
    fallback_info: data.fallbackInfo && augmentMetaToServer(data.fallbackInfo)
  };
}

